import { create } from 'zustand';
import { Chat, ChatMessage } from '../types';
import {
    createChat,
    getUserChats,
    addMessage,
    getChatMessages,
    archiveChat,
    updateTokenUsage,
    updateChatTitle,
    updateChatModel,
    deleteChat
} from '../services/firebase/chat';
import { checkUsage, checkAndIncrementUsage } from '../services/firebase/user';
import { sendMessageToOpenRouter, fetchOpenRouterModels, AVAILABLE_MODELS as DEFAULT_MODELS, DEFAULT_MODEL_ID, OpenRouterModel } from '../services/api/openRouter';

interface ChatStore {
    chats: Chat[];
    currentChat: Chat | null;
    messages: ChatMessage[];
    isLoading: boolean;
    isSending: boolean;
    selectedModel: string;
    availableModels: OpenRouterModel[];
    error: string | null;

    loadUserChats: (userId: string) => Promise<void>;
    createNewChat: (userId: string, model: string) => Promise<void>;
    selectChat: (chat: Chat) => Promise<void>;
    sendMessage: (content: string) => Promise<void>;
    setSelectedModel: (modelId: string) => Promise<void>;
    loadModels: () => Promise<void>;
    deleteChat: (chatId: string) => Promise<void>;
    clearError: () => void;
}

const TOKEN_LIMIT = 10000; // Chat context limit

export const useChatStore = create<ChatStore>((set, get) => ({
    chats: [],
    currentChat: null,
    messages: [],
    isLoading: false,
    isSending: false,
    selectedModel: DEFAULT_MODEL_ID,
    availableModels: [],
    error: null,

    loadModels: async () => {
        try {
            const models = await fetchOpenRouterModels();
            if (models.length > 0) {
                set({ availableModels: models });
            } else {
                // Fallback to default models if fetch fails or returns empty
                const fallbackModels: OpenRouterModel[] = DEFAULT_MODELS.map(m => ({
                    id: m.id,
                    name: m.name,
                    context_length: m.contextWindow,
                    pricing: { prompt: '0', completion: '0' }
                }));
                set({ availableModels: fallbackModels });
            }
        } catch (error) {
            console.error('Failed to load models:', error);
            // Fallback to default models
            const fallbackModels: OpenRouterModel[] = DEFAULT_MODELS.map(m => ({
                id: m.id,
                name: m.name,
                context_length: m.contextWindow,
                pricing: { prompt: '0', completion: '0' }
            }));
            set({ availableModels: fallbackModels });
        }
    },

    loadUserChats: async (userId: string) => {
        set({ isLoading: true, error: null });
        try {
            const chats = await getUserChats(userId);
            set({ chats });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    createNewChat: async (userId: string, model: string) => {
        set({ isLoading: true, error: null });
        try {
            const chatId = await createChat(userId, model);
            const newChat: Chat = {
                id: chatId,
                userId,
                model,
                title: 'Nova Conversa',
                createdAt: Date.now(),
                updatedAt: Date.now(),
                tokenUsage: 0,
                isArchived: false
            };
            set(state => ({
                chats: [newChat, ...state.chats],
                currentChat: newChat,
                messages: [],
                selectedModel: model
            }));
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    selectChat: async (chat: Chat) => {
        const { availableModels } = get();
        console.log('Selecting chat:', chat.id, 'Model:', chat.model);
        console.log('Available models:', availableModels.map(m => m.id));

        // Check if model exists in available models
        const modelExists = availableModels.some(m => m.id === chat.model);
        if (!modelExists) {
            console.warn(`Model ${chat.model} not found in available models!`);
        }

        set({ currentChat: chat, selectedModel: chat.model, isLoading: true, error: null });
        try {
            const messages = await getChatMessages(chat.id);
            set({ messages });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    sendMessage: async (content: string) => {
        const { currentChat, messages } = get();
        if (!currentChat) return;

        if (currentChat.isArchived) {
            set({ error: 'Esta conversa está arquivada. Inicie uma nova conversa.' });
            return;
        }

        // Check user limits first
        try {
            await checkUsage(currentChat.userId);
        } catch (error: any) {
            set({ error: error.message });
            return;
        }

        if (currentChat.tokenUsage >= TOKEN_LIMIT) {
            await archiveChat(currentChat.id);
            set(state => ({
                currentChat: { ...state.currentChat!, isArchived: true },
                chats: state.chats.map(c => c.id === currentChat.id ? { ...c, isArchived: true } : c),
                error: 'Limite de tokens da conversa atingido. Conversa arquivada.'
            }));
            return;
        }

        set({ isSending: true, error: null });

        try {
            // Add user message
            const userMessage = await addMessage(currentChat.id, {
                role: 'user',
                content
            });

            set(state => ({ messages: [...state.messages, userMessage] }));

            // Get AI response
            const aiResponseContent = await sendMessageToOpenRouter(
                currentChat.model,
                [...messages, userMessage]
            );

            // Add AI message
            const aiMessage = await addMessage(currentChat.id, {
                role: 'assistant',
                content: aiResponseContent
            });

            set(state => ({ messages: [...state.messages, aiMessage] }));

            // Update token usage (approximate: 1 token ~ 4 chars)
            const totalTokens = (content.length + aiResponseContent.length) / 4;
            const newUsage = currentChat.tokenUsage + totalTokens;

            // Update chat usage
            await updateTokenUsage(currentChat.id, newUsage);

            // Update user usage
            await checkAndIncrementUsage(currentChat.userId, totalTokens);

            // Update chat title if it's the first message
            if (messages.length === 0) {
                const newTitle = content.slice(0, 30) + (content.length > 30 ? '...' : '');
                await updateChatTitle(currentChat.id, newTitle);
                set(state => ({
                    currentChat: { ...state.currentChat!, title: newTitle, tokenUsage: newUsage },
                    chats: state.chats.map(c => c.id === currentChat.id ? { ...c, title: newTitle, tokenUsage: newUsage } : c)
                }));
            } else {
                set(state => ({
                    currentChat: { ...state.currentChat!, tokenUsage: newUsage },
                    chats: state.chats.map(c => c.id === currentChat.id ? { ...c, tokenUsage: newUsage } : c)
                }));
            }

        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isSending: false });
        }
    },

    setSelectedModel: async (modelId: string) => {
        const { currentChat } = get();
        set({ selectedModel: modelId });

        // Se há uma conversa ativa, atualiza o modelo no Firebase
        if (currentChat) {
            try {
                await updateChatModel(currentChat.id, modelId);
                set(state => ({
                    currentChat: state.currentChat ? { ...state.currentChat, model: modelId } : null,
                    chats: state.chats.map(c =>
                        c.id === currentChat.id ? { ...c, model: modelId } : c
                    )
                }));
            } catch (error) {
                console.error('Erro ao atualizar modelo do chat:', error);
            }
        }
    },

    deleteChat: async (chatId: string) => {
        set({ isLoading: true, error: null });
        try {
            await deleteChat(chatId);
            set(state => {
                const updatedChats = state.chats.filter(c => c.id !== chatId);
                const isCurrentChat = state.currentChat?.id === chatId;
                return {
                    chats: updatedChats,
                    currentChat: isCurrentChat ? null : state.currentChat,
                    messages: isCurrentChat ? [] : state.messages
                };
            });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    clearError: () => set({ error: null })
}));
